import { Box, TextField as MuiTextField } from '@mui/material';
import Grid from '@mui/material/Grid2';
import ChartHelpTooltip from '../common/ChartHelpTooltip';
import { ChangeEvent, memo, useEffect, useState } from 'react';
import { useFormContext } from '../../contexts/FormContext';
import { useFieldValue } from '../../hooks/useFormValues';
import useDebouncedCallback from '../../hooks/useDebouncedCallback';
import type { FieldComponentProps } from '../form-engine/types';

const TextField = ({ field }: FieldComponentProps) => {
  const { handleChange } = useFormContext();
  const formValue = useFieldValue(field.name!);
  const [inputValue, setInputValue] = useState(formValue ?? '');

  const debouncedHandleChange = useDebouncedCallback((value: string) => {
    handleChange(
      {
        target: {
          name: field.name!,
          value,
          type: field.type,
          checked: false,
        },
      },
      field,
    );
  }, 500);

  useEffect(() => {
    setInputValue(formValue ?? '');
  }, [formValue]);

  const handleTextChange = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const value = e.target.value;
    setInputValue(value);
    debouncedHandleChange(value);
  };

  const label = Object.prototype.hasOwnProperty.call(field, 'help') ? (
    <>
      {field.label}
      <Box component="span" sx={{ display: 'inline-flex' }}>
        <ChartHelpTooltip chartType={field.help!} type="field" />
      </Box>
    </>
  ) : (
    field.label
  );

  return (
    <Grid size={field.size || 12} key={field.name}>
      <MuiTextField
        label={label}
        fullWidth
        required={field.required || false}
        disabled={!!field.disabled}
        multiline={(field.multiline as boolean) || false}
        rows={(field.rows as number) || 1}
        type={field.type}
        name={field.name}
        value={inputValue}
        onChange={handleTextChange}
      />
    </Grid>
  );
};

export default memo(TextField);
